import { useEffect } from "react";
import { Link } from "react-router-dom";
import { UserPlus, PackagePlus, Radio, MapPin, Camera } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import { PrimaryButton } from "@/components/brand";
import { useLang, t, withLang } from "@/lib/i18n";

const STEPS = [
  {
    index: "01", icon: UserPlus,
    kicker: { en: "Onboarding", fr: "Intégration" },
    title: { en: "Merchants sign up and pick a plan.", fr: "Les marchands s'inscrivent et choisissent un forfait." },
    body: {
      en: "Create your merchant account in a couple of minutes: contact details, business address, and the plan that fits your delivery volume. Every new account starts in Pending Approval so our operations team can verify your business — most approvals land within one business day.",
      fr: "Créez votre compte marchand en quelques minutes : coordonnées, adresse d'affaires et forfait adapté à votre volume. Chaque nouveau compte démarre à l'état « en attente d'approbation » afin que notre équipe puisse vérifier votre entreprise — la plupart des approbations arrivent en un jour ouvrable.",
    },
    highlights: [
      { en: "Sign up in under 3 minutes", fr: "Inscription en moins de 3 minutes" },
      { en: "Choose Starter, Growth, or Scale", fr: "Choisissez Starter, Growth ou Scale" },
      { en: "Get approved by ops within a business day", fr: "Approbation en un jour ouvrable" },
    ],
  },
  {
    index: "02", icon: PackagePlus,
    kicker: { en: "Booking", fr: "Réservation" },
    title: { en: "Merchants book a delivery from the console.", fr: "Les marchands créent une livraison depuis la console." },
    body: {
      en: "Enter the pickup and dropoff, the package details, and the customer's phone number. Routa validates the addresses, geocodes them, and drops the order straight into dispatch — no CSV imports, no third-party integrations required.",
      fr: "Saisissez le ramassage et la livraison, les détails du colis et le numéro du client. Routa valide et géocode les adresses, puis dépose la commande directement dans la répartition — aucun import CSV, aucune intégration tierce requise.",
    },
    highlights: [
      { en: "Pickup + dropoff with live geocoding", fr: "Ramassage et livraison avec géocodage en direct" },
      { en: "Package notes, size, and time window", fr: "Notes de colis, taille et fenêtre horaire" },
      { en: "Customer phone → automatic SMS updates", fr: "Numéro du client → mises à jour SMS automatiques" },
    ],
  },
  {
    index: "03", icon: Radio,
    kicker: { en: "Assignment", fr: "Attribution" },
    title: { en: "Dispatch assigns the right driver instantly.", fr: "La répartition attribue le bon livreur instantanément." },
    body: {
      en: "The routing engine suggests the optimal driver for every new order. A dispatcher confirms with one click and the driver is notified everywhere at once — SMS, email, and a push notification on their PWA home-screen icon.",
      fr: "Le moteur de routage propose le meilleur livreur pour chaque nouvelle commande. Le répartiteur confirme en un clic et le livreur est prévenu partout à la fois — SMS, courriel et notification poussée sur l'icône PWA de son écran d'accueil.",
    },
    highlights: [
      { en: "AI-suggested driver match", fr: "Suggestion de livreur par l'IA" },
      { en: "One-click dispatch override", fr: "Réattribution en un clic" },
      { en: "Driver notified by SMS + push + email", fr: "Livreur prévenu par SMS + notification + courriel" },
    ],
  },
  {
    index: "04", icon: MapPin,
    kicker: { en: "Live tracking", fr: "Suivi en direct" },
    title: { en: "Drivers follow an AI-optimized route.", fr: "Les livreurs suivent un trajet optimisé par l'IA." },
    body: {
      en: "The driver PWA sequences the stops for the fastest realistic path across the fleet and updates in real time as new orders come in or drivers go offline. The public tracking link shows customers a live map with the driver's first name and ETA — nothing more, nothing less.",
      fr: "Le PWA du livreur séquence les arrêts pour obtenir le trajet le plus rapide et se met à jour en temps réel selon les nouvelles commandes ou les livreurs hors ligne. Le lien de suivi public affiche aux clients une carte en direct avec le prénom du livreur et l'heure d'arrivée — rien de plus, rien de moins.",
    },
    highlights: [
      { en: "Live driver location + ETA", fr: "Position du livreur et heure d'arrivée en direct" },
      { en: "Route re-sequences when the fleet changes", fr: "Les trajets se recalculent quand la flotte change" },
      { en: "Privacy-safe customer tracking link", fr: "Lien de suivi respectueux de la vie privée" },
    ],
  },
  {
    index: "05", icon: Camera,
    kicker: { en: "Proof of delivery", fr: "Preuve de livraison" },
    title: { en: "Drivers capture proof, customers get closure.", fr: "Le livreur capture la preuve, le client reçoit sa confirmation." },
    body: {
      en: "At the door the driver captures a photo and a signature from the driver PWA. Customers get an SMS the moment the delivery is complete, and merchants see the receipt in their order timeline — no chasing needed.",
      fr: "À la porte, le livreur capture une photo et une signature depuis le PWA. Les clients reçoivent un SMS dès la livraison, et les marchands voient la preuve dans la chronologie de la commande — plus besoin de courir après.",
    },
    highlights: [
      { en: "Photo + signature at dropoff", fr: "Photo et signature à la livraison" },
      { en: "Automatic SMS to the customer", fr: "SMS automatique au client" },
      { en: "Full audit trail in the merchant timeline", fr: "Piste d'audit complète dans la chronologie du marchand" },
    ],
  },
];

export default function HowItWorks() {
  const lang = useLang();
  useEffect(() => {
    document.title = lang === "fr" ? "Routa · Comment ça marche" : "Routa · How it works";
  }, [lang]);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="how-it-works-page">
      <MarketingNav />

      <section className="mx-auto max-w-[1400px] w-full px-6 lg:px-10 pt-14 pb-8 lg:pt-24 lg:pb-14">
        <div className="flex items-center gap-4 mb-6">
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">
            {t(lang, { en: "[00] How it works", fr: "[00] Comment ça marche" })}
          </span>
          <span className="h-px w-16 bg-gray-300" />
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">
            {t(lang, { en: "Merchant → Driver → Customer", fr: "Marchand → Livreur → Client" })}
          </span>
        </div>
        <h1 className="headline text-4xl sm:text-5xl lg:text-6xl text-[#0a0a0a] max-w-4xl">
          {t(lang, {
            en: <>Five steps from your <span className="text-orange-500">store</span> to your customer's door.</>,
            fr: <>Cinq étapes de votre <span className="text-orange-500">boutique</span> à la porte de votre client.</>,
          })}
        </h1>
        <p className="mt-6 text-base text-gray-500 max-w-xl leading-relaxed">
          {t(lang, {
            en: "Routa is built around a simple operational story. Every part of the platform — merchant console, driver PWA, dispatch view, and public tracking — lives on the same rail so orders never fall through the cracks.",
            fr: "Routa repose sur une histoire opérationnelle simple. Chaque volet — console marchand, PWA livreur, vue de répartition et suivi public — vit sur le même rail pour qu'aucune commande ne passe entre les mailles.",
          })}
        </p>
      </section>

      <section className="mx-auto max-w-[1400px] w-full px-6 lg:px-10 pb-16 space-y-16 lg:space-y-24">
        {STEPS.map((step, i) => (
          <div key={step.index} data-testid={`hiw-step-${step.index}`} className="grid lg:grid-cols-[220px_1fr] gap-6 lg:gap-14">
            <div>
              <div className="flex lg:block items-center gap-4">
                <div className="font-mono text-[64px] lg:text-[96px] leading-none font-black text-[#0a0a0a]">
                  {step.index}
                </div>
                <div className="mt-2 hidden lg:block h-px w-16 bg-gray-300" />
              </div>
              <div className="mt-4 font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400">{t(lang, step.kicker)}</div>
            </div>
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="grid place-items-center h-10 w-10 border border-gray-200">
                  <step.icon className="h-5 w-5 text-orange-500" />
                </div>
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400">
                  {t(lang, { en: `Step ${i + 1} of 5`, fr: `Étape ${i + 1} sur 5` })}
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0a0a0a] leading-tight">
                {t(lang, step.title)}
              </h2>
              <p className="mt-4 text-base text-gray-600 leading-relaxed">{t(lang, step.body)}</p>
              <ul className="mt-6 space-y-2.5">
                {step.highlights.map((h, hi) => (
                  <li key={hi} className="flex items-start gap-3 text-sm text-gray-600">
                    <span className="mt-2 h-1.5 w-1.5 bg-orange-500 shrink-0" />
                    <span>{t(lang, h)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}

        <div className="border border-gray-200 p-8 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6" data-testid="hiw-cta">
          <div>
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400 mb-3">
              {t(lang, { en: "[06] Ready?", fr: "[06] Prêt?" })}
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-[#0a0a0a] max-w-xl">
              {t(lang, { en: "Start dispatching, or drive with Routa.", fr: "Commencez à répartir, ou livrez avec Routa." })}
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to={withLang("/register", lang)}>
              <PrimaryButton orange arrow data-testid="hiw-merchant-cta">
                {t(lang, { en: "Start delivering", fr: "Commencer à livrer" })}
              </PrimaryButton>
            </Link>
            <Link to={withLang("/apply", lang)}>
              <PrimaryButton data-testid="hiw-driver-cta">
                {t(lang, { en: "Apply to drive", fr: "Postuler comme livreur" })}
              </PrimaryButton>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
